import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  RefreshCw, Eye, MapPin, Download, Settings, Search, X
} from 'lucide-react';
import assetModel from '../../../../models/assetModel';
import AssetDetailModal from '../../../../components/mms/modals/AssetDetailModal';

const Assets = () => {
  // State management
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0
  });
  
  // Modal states
  const [detailModal, setDetailModal] = useState({ show: false, asset: null });
  
  // Fetch assets
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        page: currentPage,
        limit: pageSize,
        active_only: activeOnly ? 1 : 0,
        search: searchTerm
      };
      
      const response = await assetModel.getWaterPumps(filters);
      
      if (response.success) {
        setAssets(response.data || []);
        setPagination({
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 0
        });
      } else {
        toast.error(response.error || 'Failed to fetch assets');
        setAssets([]);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeOnly, searchTerm]);

  // Initial load and refresh
  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchAssets();
  };

  // Handle view asset detail
  const handleViewAsset = async (assetId) => {
    try {
      const response = await assetModel.getWaterPump(assetId);
      if (response.success) {
        setDetailModal({ show: true, asset: response.data });
      } else {
        toast.error('Failed to load asset details');
      }
    } catch (error) {
      console.error('Error loading asset:', error);
      toast.error('Failed to load asset details');
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Pump ID',
      'Pump Name',
      'Station Name',
      'Model',
      'RPM',
      'Commission Date',
      'Status',
      'Latitude',
      'Longitude'
    ];

    const rows = assets.map(asset => [
      asset.PUMP_ID,
      asset.PUMP_NAME || '',
      asset.STATION_NAME || '',
      asset.PUMP_MODEL || '',
      asset.RPM || '',
      asset.COMMISSION_DATE || '',
      asset.DECOMMISSION_DATE ? 'Decommissioned' : 'Active',
      asset.latitude || '',
      asset.longitude || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `assets-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${assets.length} assets to CSV`);
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (asset) => {
    if (asset.DECOMMISSION_DATE) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-800 border-gray-300">
          Decommissioned
        </span>
      );
    }
    return (
      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-800 border-green-300">
        Active
      </span>
    );
  };

  // Pagination
  const totalPages = pagination.pages;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Assets</h1>
                <p className="text-xs text-gray-500">Water infrastructure assets management</p>
              </div>
            </div>

            {/* Action Buttons and Search */}
            <div className="flex items-center space-x-2 ml-4">
              {/* Search Bar */}
              <div className="relative w-[400px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Search by pump or station name..."
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Active Only Toggle */}
              <button
                onClick={() => {
                  setActiveOnly(!activeOnly);
                  setCurrentPage(1);
                }}
                className={`flex items-center px-3 py-2 border rounded-md text-sm transition-colors ${
                  activeOnly
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {activeOnly ? 'Active Only' : 'Show All'}
              </button>

              {/* Export Button */}
              <button
                onClick={exportToCSV}
                disabled={assets.length === 0}
                className="flex items-center px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </button>

              {/* Refresh Button */}
              <button
                onClick={fetchAssets}
                disabled={loading}
                className="flex items-center px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">
              Showing {Math.min(pageSize, assets.length)} of {pagination.total} assets
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="text-sm px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Assets Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Pump ID</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Name / Station</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Model</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">RPM</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Commissioned</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-blue-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && assets.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-3 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <RefreshCw className="animate-spin h-5 w-5 text-blue-500" />
                        <span className="text-sm text-gray-600">Loading assets...</span>
                      </div>
                    </td>
                  </tr>
                )}
                
                {!loading && assets.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-3 py-8 text-center">
                      <p className="text-gray-600">No assets found</p>
                    </td>
                  </tr>
                )}
                
                {assets.map(asset => (
                  <tr 
                    key={asset.PUMP_ID} 
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-3 py-3 text-sm font-bold text-blue-600">
                      #{asset.PUMP_ID}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      <div className="max-w-xs truncate font-medium" title={asset.PUMP_NAME}>
                        {asset.PUMP_NAME || 'N/A'}
                      </div>
                      {asset.STATION_NAME && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={asset.STATION_NAME}>
                          {asset.STATION_NAME}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {asset.PUMP_MODEL || 'N/A'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {asset.RPM || 'N/A'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {formatDate(asset.COMMISSION_DATE)}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {getStatusBadge(asset)}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleViewAsset(asset.PUMP_ID)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        
                        {asset.has_coordinates && asset.latitude && asset.longitude && (
                          <button
                            onClick={() => window.open(`https://www.google.com/maps?q=${asset.latitude},${asset.longitude}`, '_blank')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="View on Map"
                          >
                            <MapPin size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {assets.length > 0 && totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-sm border rounded ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {detailModal.show && detailModal.asset && (
        <AssetDetailModal
          asset={detailModal.asset}
          onClose={() => setDetailModal({ show: false, asset: null })}
        />
      )}
    </div>
  );
};

export default Assets;
